import {
  Component,
  computed,
  DOCUMENT,
  effect,
  inject,
  Inject,
  Injector,
  NgZone,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import {ConfigurationStore} from './store/configuration.store';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {filter} from 'rxjs';
import {AsyncPipe, isPlatformBrowser} from '@angular/common';
import {HideFloatingCartService} from './services/hide-floating-cart.service';
import {Title} from '@angular/platform-browser';
import {CartService} from './services/cart.service';
import {MatDialog} from '@angular/material/dialog';
import {LoginComponent} from './login/login.component';
import {OrderComponent} from './order/order.component';
import {OrderContextStore} from './store/order-context.store';
import {SharedDialogComponent} from './shared-dialog/shared-dialog.component';
import {ThemeService} from './services/theme.service';
import {environment} from '../environments/environment';
import {AnalyticsService} from './services/analytics.service';
import {ConfigurationService} from './services/configuration.service';
import {CartConfigStore} from './store/cart-config.store';
import {FooterComponent} from './shared/footer/footer.component';
import {FloatingCartComponent} from './floating-cart/floating-cart.component';
import {CookiesComponent} from './cookies/cookies.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [
    RouterOutlet,
    AsyncPipe,
    FooterComponent,
    FloatingCartComponent,
    CookiesComponent
  ]
})
export class AppComponent implements OnInit {
  title = 'pizza-pro';
  private readonly configStore = inject(ConfigurationStore);
  readonly socials = this.configStore.socials;
  readonly name = this.configStore.name;
  readonly logo = this.configStore.logo;
  private configService = inject(ConfigurationService);
  hideFloatingCartService = inject(HideFloatingCartService);
  orderContextStore = inject(OrderContextStore);
  cartService = inject(CartService);
  injector = inject(Injector);
  hasOpenDialog = false;
  private themeService = inject(ThemeService);
  private analyticsService = inject(AnalyticsService);
  private orderDialogOpened = false;
  private cartConfigStore = inject(CartConfigStore);
  cartConfig = this.cartConfigStore.cartConfig;
  isCartConfigLoaded = this.cartConfigStore.isLoaded;

  constructor(
    private router: Router,
    private titleService: Title,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object,
    private dialog: MatDialog,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone
  ) {
    this.applyTheme();

    // Effet 1 : Favicon logo
    effect(() => {
      const logoUrl = this.configStore.logo();
      if (logoUrl && isPlatformBrowser(this.platformId)) {
        let link: HTMLLinkElement | null = this.document.querySelector('link[rel="icon"]');
        if (!link) {
          link = this.document.createElement('link');
          link.rel = 'icon';
          this.document.head.appendChild(link);
        }
        link.href = logoUrl;
        link.type = 'image/png';
      }
    });

    // Effet 2 : Ouverture automatique du type de commande (Modal)
    effect(() => {
      const items = this.cartService.items();
      if (isPlatformBrowser(this.platformId) && items.length === 0 && this.router.url.includes('/product')) {
        const noContext = !localStorage.getItem('order_context_type') &&
          this.orderContextStore.orderType() === null;

        if (noContext && this.dialog.openDialogs.length === 0 && !this.orderDialogOpened) {
          this.orderDialogOpened = true;
          const ref = this.openOrderTypeDialog();
          ref.afterClosed().subscribe(() => {
            this.orderDialogOpened = false;
          });
        }
      }
    }, {injector: this.injector});

    // Effet 3 : Réactivité automatique si la config du panier change (ex: désactivation dynamique)
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const isCommandRoute = this.router.url.includes('/command');

      if (isCommandRoute && this.isCartDisabled()) {
        this.blockCommandAccess();
      }
    }, {injector: this.injector});

    // Effet 4 : Alerte promotion supprimée
    effect(() => {
      const notification = this.cartService.removedPromoNotification();
      if (!notification || !isPlatformBrowser(this.platformId)) return;

      this.cartService.removedPromoNotification.set(null);

      this.dialog.open(SharedDialogComponent, {
        panelClass: 'custom-dialog',
        disableClose: true,
        data: {
          context: 'promotion-warning',
          title: notification.title,
          message: notification.message,
          promoImage: null
        }
      });
    }, {injector: this.injector});
  }
  ngOnInit() {
    this.cartConfigStore.loadConfig();
    this.ngZone.runOutsideAngular(() => {
      this.cartService.startPromoExpirationWatcher();
    });

    this.route.queryParams.subscribe(params => {
      const key = params['key'];
      const token = params['token'];
      const coupon = params['coupon'];
      if (key && token) {
        this.openResetPasswordDialog(key, token);
        this.router.navigate([], {
          queryParams: {},
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
      if (coupon) {
        if (!sessionStorage.getItem('pending_coupon')) {
          sessionStorage.setItem('pending_coupon', coupon);
        }
        this.dialog.open(OrderComponent, {
          panelClass: 'custom-dialog',
          autoFocus: false,
          disableClose: true,
        });
        this.router.navigate([], {
          queryParams: {coupon: null},
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (isPlatformBrowser(this.platformId)) {
        this.ngZone.runOutsideAngular(() => {
          this.scrollToTopSmooth();
        });

        // Verification lors de la navigation directe vers /command
        if (event.urlAfterRedirects.includes('/command')) {
          if (this.isCartDisabled()) {
            this.blockCommandAccess();
            return;
          }

          const items = this.cartService.items();
          const noContext = !localStorage.getItem('order_context_type') &&
            this.orderContextStore.orderType() === null;

          if (items.length === 0 && noContext) {
            this.router.navigate(['/']);
          }
        } else {
          localStorage.removeItem('order_discount');
          this.cartService.setOrderDiscount(null);
        }

        this.checkIfShouldOpenOrderTypeDialog(event.urlAfterRedirects);
      }
    });

    this.dialog.afterOpened.subscribe(() => {
      this.hasOpenDialog = this.dialog.openDialogs.length > 0;
    });

    this.dialog.afterAllClosed.subscribe(() => {
      this.hasOpenDialog = false;
    });
  }


  // Redirection et affichage de la popup d'avertissement
  private blockCommandAccess(): void {
    this.router.navigate(['/']);

    if (this.dialog.openDialogs.some(d => d.componentInstance instanceof SharedDialogComponent)) {
      return;
    }

    this.dialog.open(SharedDialogComponent, {
      panelClass: 'custom-dialog',
      width: '420px',
      disableClose: true,
      data: {
        context: 'cart-disabled',
        title: 'Commandes désactivées 🛒',
        message: `${this.getDisabledPeriodMessage()} Vous avez été redirigé vers l'accueil.`
      }
    });
  }
  private parseLocalDate(dateStr?: string): Date | null {
    if (!dateStr) return null;
    const cleanStr = dateStr.split('.')[0].replace('Z', '');
    const parts = cleanStr.split('T');
    if (parts.length !== 2) return new Date(dateStr);

    const [year, month, day] = parts[0].split('-').map(Number);
    const [hours, minutes, seconds] = parts[1].split(':').map(Number);

    return new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0);
  }

  // 3. Extraction brute de l'heure "HH:mm" sans conversion
  private formatTimeDirect(dateStr?: string): string {
    if (!dateStr) return '';
    const timePart = dateStr.split('T')[1];
    return timePart ? timePart.substring(0, 5) : '';
  }

  // 4. Extraction brute de la date "DD/MM/YYYY" sans conversion
  private formatDateDirect(dateStr?: string): string {
    if (!dateStr) return '';
    const datePart = dateStr.split('T')[0];
    if (!datePart) return '';
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  }

  // 5. Calcul réactif de désactivation du panier
  isCartDisabled = computed(() => {
    const cart = this.cartConfig();
    if (!cart) return false;
    if (cart.active) return false;

    const now = new Date().getTime();
    const start = this.parseLocalDate(cart.startDate)?.getTime() ?? null;
    const end = this.parseLocalDate(cart.endDate)?.getTime() ?? null;

    if (start && end) return now >= start && now <= end;
    if (start) return now >= start;
    if (end) return now >= end;

    return true;
  });

  // 6. Message d'information identique à TopNavbarComponent
  getDisabledPeriodMessage = computed<string>(() => {
    const cart = this.cartConfig();
    const contactSuffix = " Merci de contacter l'administration.";

    if (!cart) return 'Les commandes en ligne sont actuellement désactivées.';

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const startRaw = cart.startDate?.split('T')[0];
    const endRaw = cart.endDate?.split('T')[0];

    const startTimeStr = this.formatTimeDirect(cart.startDate);
    const endTimeStr = this.formatTimeDirect(cart.endDate);

    const startDateFormatted = this.formatDateDirect(cart.startDate);
    const endDateFormatted = this.formatDateDirect(cart.endDate);

    if (startRaw && endRaw) {
      const startIsToday = startRaw === todayStr;
      const endIsToday = endRaw === todayStr;

      if (startIsToday && endIsToday) {
        return `🛒 Les commandes en ligne sont désactivées de ${startTimeStr} à ${endTimeStr}.` + contactSuffix;
      }

      if (startIsToday) {
        return `🛒 Les commandes en ligne sont désactivées à partir de ${startTimeStr} jusqu'au ${endDateFormatted} à ${endTimeStr}.` + contactSuffix;
      }

      return `🛒 Les commandes en ligne sont désactivées du ${startDateFormatted} à ${startTimeStr} au ${endDateFormatted} à ${endTimeStr}.` + contactSuffix;
    }

    if (startRaw) {
      if (startRaw === todayStr) {
        return `🛒 Les commandes en ligne sont désactivées à partir de ${startTimeStr}.` + contactSuffix;
      }
      return `🛒 Les commandes en ligne sont désactivées à partir du ${startDateFormatted} à ${startTimeStr}.` + contactSuffix;
    }

    if (endRaw) {
      if (endRaw === todayStr) {
        return `🛒 Les commandes en ligne sont désactivées jusqu'à ${endTimeStr}.` + contactSuffix;
      }
      return `🛒 Les commandes en ligne sont désactivées jusqu'au ${endDateFormatted} à ${endTimeStr}.` + contactSuffix;
    }

    return '🛒 Les commandes en ligne sont temporairement désactivées.';
  });

  private checkIfShouldOpenOrderTypeDialog(currentUrl: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!currentUrl.includes('/product')) return;

    const hasOrderContextInStorage =
      !!localStorage.getItem('order_context_type') ||
      !!localStorage.getItem('order_context') ||
      !!sessionStorage.getItem('order_context');

    const storeHasContext = this.orderContextStore.orderType() !== null &&
      this.orderContextStore.orderType() !== undefined;

    if (!hasOrderContextInStorage && !storeHasContext) {
      if (this.orderDialogOpened || this.dialog.openDialogs.length > 0) return;
      this.orderDialogOpened = true;
      const ref = this.openOrderTypeDialog();
      ref.afterClosed().subscribe(() => {
        this.orderDialogOpened = false;
      });
    } else {
      this.orderDialogOpened = false;
    }
  }

  private openOrderTypeDialog(): any {
    return this.dialog.open(OrderComponent, {
      panelClass: 'custom-dialog',
      autoFocus: false,
      disableClose: true,
      data: {
        stayOnProductPage: true,
        hideCancelButton: true
      }
    });
  }

  private openResetPasswordDialog(key: string, token: string) {
    const dialogRef = this.dialog.open(LoginComponent, {
      disableClose: true,
      data: {
        isResetPasswordMode: true,
        resetKey: key,
        resetToken: token
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.router.navigate(['/profil']);
      }
    });
  }

  private scrollToTopSmooth(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });

      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }, 100);

        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 150);
      });
    }
  }

  private applyTheme(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (environment.theme?.mode === 'dark') {
      this.document.documentElement.classList.add('dark');
    } else {
      this.document.documentElement.classList.remove('dark');
    }
  }

  getSocialIcon(url: string): string {
    const socialIcons: { [key: string]: string } = {
      'facebook.com': 'fab fa-facebook-f',
      'instagram.com': 'fab fa-instagram',
      'twitter.com': 'fab fa-twitter',
      'x.com': 'fab fa-twitter',
      'linkedin.com': 'fab fa-linkedin-in',
      'youtube.com': 'fab fa-youtube',
      'tiktok.com': 'fab fa-tiktok',
      'pinterest.com': 'fab fa-pinterest',
      'snapchat.com': 'fab fa-snapchat'
    };

    for (const [domain, icon] of Object.entries(socialIcons)) {
      if (url.includes(domain)) {
        return icon;
      }
    }

    return 'fas fa-share-alt';
  }
}
